import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, Pencil, MessageCircle, Phone, MapPin,
  Vote, Tag, FileText, Clock, Mic,
  CheckCircle2, XCircle, Loader2, MoreHorizontal,
  UserCheck, Sparkles, Copy, Check,
} from 'lucide-react';
import { useContact, useContactCategories, useContactTags } from '../hooks/useContacts';
import { useUpdateContact } from '../hooks/useContactMutations';
import { ContactBadge, TagBadge, EngagementBar } from './ContactBadge';
import { ContactForm } from './ContactForm';
import { VoiceRecorder } from './voice-notes/VoiceRecorder';
import { VoiceNotesTimeline } from './voice-notes/VoiceNotesTimeline';
import { GENDER_LABELS } from '../types';
import type { ContactInsert } from '../types';

/* ─── helpers ─────────────────────────────────────────────────────────────── */
function fmtDate(iso: string | null | undefined) {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'long' }).format(new Date(iso));
}
function fmtDateTime(iso: string) {
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(iso));
}
function openWA(phone: string, name: string) {
  const n = phone.replace(/\D/g, '');
  const num = n.startsWith('55') ? n : `55${n}`;
  window.open(`https://wa.me/${num}?text=${encodeURIComponent(`Olá, ${name}!`)}`, '_blank', 'noopener');
}
function initials(name: string) {
  return name.split(' ').slice(0, 2).map((p) => p[0]).join('').toUpperCase();
}

/* ─── Seção com título ────────────────────────────────────────────────────── */
function Section({ icon: Icon, title, children }: {
  icon: React.ElementType; title: string; children: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl card-premium p-5 md:p-6 space-y-4">
      <div className="flex items-center gap-2 hairline-b pb-3">
        <Icon className="h-4 w-4 text-accent" />
        <h3 className="font-display text-sm font-bold text-ink">{title}</h3>
      </div>
      {children}
    </div>
  );
}

/* ─── Linha de dado ───────────────────────────────────────────────────────── */
function DataRow({ label, value, href, mono }: {
  label: string; value: string | null | undefined; href?: string; mono?: boolean;
}) {
  if (!value) return null;
  return (
    <div className="flex items-start justify-between gap-4 py-1.5">
      <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-ink-subtle whitespace-nowrap shrink-0">
        {label}
      </span>
      {href ? (
        <a href={href} target="_blank" rel="noopener"
          className={`text-right text-sm text-accent hover:underline truncate ${mono ? 'font-mono' : ''}`}>
          {value}
        </a>
      ) : (
        <span className={`text-right text-sm text-ink truncate ${mono ? 'font-mono text-[12px]' : ''}`}>
          {value}
        </span>
      )}
    </div>
  );
}

/* ─── Copiável ─────────────────────────────────────────────────────────────── */
function CopyableUUID({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    void navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button onClick={copy}
      className="flex items-center gap-1.5 font-mono text-[10px] text-ink-faint hover:text-ink-subtle transition-colors group">
      <span className="truncate max-w-[180px]">{value}</span>
      {copied
        ? <Check className="h-3 w-3 text-emerald-400 shrink-0" />
        : <Copy className="h-3 w-3 shrink-0 opacity-0 group-hover:opacity-100" />}
    </button>
  );
}

/* ─── Modal de edição ─────────────────────────────────────────────────────── */
function EditModal({ contactId, onClose }: { contactId: string; onClose: () => void }) {
  const { data: contact } = useContact(contactId);
  const update = useUpdateContact();

  const handleSubmit = (data: ContactInsert) => {
    update.mutate({ id: contactId, payload: data }, { onSuccess: onClose });
  };

  if (!contact) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 backdrop-blur-sm p-4 md:p-8">
      <div className="w-full max-w-2xl animate-fade-in">
        <ContactForm
          contact={contact}
          onSubmit={handleSubmit}
          onClose={onClose}
          isPending={update.isPending}
        />
      </div>
    </div>
  );
}

/* ─── Página principal ────────────────────────────────────────────────────── */
export function ContactDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [editOpen, setEditOpen] = useState(false);
  const [notesEdit, setNotesEdit] = useState(false);
  const [notesDraft, setNotesDraft] = useState('');

  const { data: contact, isLoading, isError } = useContact(id ?? null);
  const { data: categories = [] } = useContactCategories();
  const { data: allTags = [] } = useContactTags();
  const update = useUpdateContact();

  /* ── loading ── */
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-accent" />
      </div>
    );
  }

  /* ── erro / não encontrado ── */
  if (isError || !contact) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <XCircle className="h-12 w-12 text-danger/60" />
        <p className="text-ink-muted">Contato não encontrado.</p>
        <button onClick={() => navigate('/app/contatos')} className="btn-ghost px-4 py-2 rounded-xl text-sm">
          Voltar aos Contatos
        </button>
      </div>
    );
  }

  const hasAddress = contact.street || contact.neighborhood || contact.city;
  const hasElectoral = contact.electoral_zone || contact.electoral_section || contact.voter_registration;
  const mapsQuery = [contact.street, contact.street_number, contact.neighborhood, contact.city]
    .filter(Boolean).join(', ');

  const handleSaveNotes = () => {
    update.mutate({ id: contact.id, payload: { notes: notesDraft } }, {
      onSuccess: () => setNotesEdit(false),
    });
  };

  const toggleActive = () => {
    update.mutate({ id: contact.id, payload: { active: !contact.active } });
  };

  return (
    <>
      <div className="max-w-6xl mx-auto space-y-6 pb-16 animate-fade-in">

        {/* ── Topbar ── */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/app/contatos')}
            className="flex items-center gap-1.5 rounded-xl btn-ghost px-3 py-2 text-sm font-medium"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Contatos</span>
          </button>
          <span className="text-ink-subtle">/</span>
          <span className="text-sm font-semibold text-ink truncate">{contact.full_name}</span>

          <div className="ml-auto flex items-center gap-2">
            {/* WhatsApp */}
            {contact.phone && contact.phone_whatsapp && (
              <button
                onClick={() => openWA(contact.phone!, contact.full_name)}
                className="flex items-center gap-1.5 rounded-xl px-3 py-2 text-sm font-semibold text-emerald-400 border border-emerald-500/25 bg-emerald-500/10 hover:bg-emerald-500/20 transition-colors cursor-pointer"
              >
                <MessageCircle className="h-4 w-4" />
                <span className="hidden sm:inline">WhatsApp</span>
              </button>
            )}
            {/* Editar */}
            <button
              onClick={() => setEditOpen(true)}
              className="flex items-center gap-1.5 rounded-xl btn-accent px-3 py-2 text-sm font-semibold"
            >
              <Pencil className="h-3.5 w-3.5" />
              <span>Editar</span>
            </button>
            {/* Mais opções */}
            <div className="relative group">
              <button className="rounded-xl btn-ghost p-2 text-ink-muted hover:text-ink transition-colors">
                <MoreHorizontal className="h-4 w-4" />
              </button>
              <div className="absolute right-0 top-full mt-1 w-48 rounded-2xl glass-strong shadow-deep overflow-hidden z-20 hidden group-hover:block animate-fade-in">
                <button
                  onClick={toggleActive}
                  className="flex w-full items-center gap-2.5 px-4 py-3 text-sm hover:bg-white/5 transition-colors cursor-pointer text-left"
                  style={{ color: contact.active ? 'var(--color-danger)' : 'var(--color-success)' }}
                >
                  {contact.active
                    ? <><XCircle className="h-4 w-4" /><span>Desativar contato</span></>
                    : <><CheckCircle2 className="h-4 w-4" /><span>Reativar contato</span></>}
                </button>
                <Link
                  to={`/app/copiloto`}
                  className="flex w-full items-center gap-2.5 px-4 py-3 text-sm hover:bg-white/5 transition-colors text-ink-muted"
                >
                  <Sparkles className="h-4 w-4 text-accent" />
                  <span>Perguntar ao Lumen</span>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* ── Hero do contato ── */}
        <div className="relative overflow-hidden rounded-3xl glass-strong shadow-deep p-6 md:p-8">
          <div
            className="absolute -top-24 -right-24 h-64 w-64 rounded-full blur-3xl pointer-events-none opacity-30"
            style={{ background: 'color-mix(in oklab, var(--color-accent), transparent 60%)' }}
          />
          <div className="relative flex flex-col sm:flex-row items-start sm:items-center gap-5">
            {/* Avatar */}
            <div className="relative shrink-0">
              <div className="absolute inset-0 rounded-2xl blur-xl bg-accent/20" />
              <div className="relative h-20 w-20 rounded-2xl bg-gradient-to-br from-accent/30 to-accent/10 hairline flex items-center justify-center font-display text-2xl font-extrabold text-accent">
                {initials(contact.full_name)}
              </div>
            </div>

            {/* Info principal */}
            <div className="flex-grow min-w-0 space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight text-ink">
                  {contact.full_name}
                </h1>
                {!contact.active && (
                  <span className="inline-flex items-center gap-1 rounded-full border border-danger/30 bg-danger/10 px-2 py-0.5 text-[10px] font-semibold text-danger uppercase tracking-wider">
                    <XCircle className="h-3 w-3" /> Inativo
                  </span>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <ContactBadge category={contact.category} categories={categories} />
                {contact.city && (
                  <span className="flex items-center gap-1 text-xs text-ink-muted">
                    <MapPin className="h-3 w-3" />
                    {[contact.neighborhood, contact.city, contact.state_code].filter(Boolean).join(', ')}
                  </span>
                )}
              </div>
              {contact.phone && (
                <div className="flex items-center gap-3">
                  <a
                    href={`tel:${contact.phone}`}
                    className="flex items-center gap-1.5 text-sm text-ink-muted hover:text-ink transition-colors"
                  >
                    <Phone className="h-3.5 w-3.5" />
                    {contact.phone}
                  </a>
                  {contact.phone_whatsapp && (
                    <span className="text-[10px] font-semibold text-emerald-400 bg-emerald-500/10 border border-emerald-500/25 rounded-full px-2 py-0.5">
                      WhatsApp
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Engajamento */}
            <div className="shrink-0 flex flex-col items-center gap-2 rounded-2xl bg-midnight-2/60 hairline px-5 py-4 min-w-[120px]">
              <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-ink-subtle">
                Engajamento
              </span>
              <span className="font-display text-3xl font-extrabold text-ink">
                {contact.engagement_score}
              </span>
              <EngagementBar score={contact.engagement_score} />
            </div>
          </div>
        </div>

        {/* ── Layout duas colunas ── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* Coluna principal (2/3) */}
          <div className="lg:col-span-2 space-y-5">

            {/* Contato */}
            <Section icon={Phone} title="Informações de contato">
              <div className="divide-y divide-hairline">
                <DataRow label="Telefone" value={contact.phone}
                  href={contact.phone ? `tel:${contact.phone}` : undefined} />
                <DataRow label="E-mail" value={contact.email}
                  href={contact.email ? `mailto:${contact.email}` : undefined} />
                <DataRow label="Gênero"
                  value={contact.gender ? GENDER_LABELS[contact.gender] : null} />
                <DataRow label="Nascimento" value={fmtDate(contact.birth_date)} />
                <DataRow label="CPF" value={contact.cpf} mono />
              </div>
            </Section>

            {/* Endereço */}
            {hasAddress && (
              <Section icon={MapPin} title="Endereço">
                <div className="divide-y divide-hairline">
                  {contact.street && (
                    <DataRow
                      label="Logradouro"
                      value={[contact.street, contact.street_number, contact.complement]
                        .filter(Boolean).join(', ')}
                    />
                  )}
                  <DataRow label="Bairro" value={contact.neighborhood} />
                  <DataRow label="Cidade / UF"
                    value={[contact.city, contact.state_code].filter(Boolean).join(' — ')} />
                  <DataRow label="CEP" value={contact.zip_code} mono />
                </div>
                {mapsQuery && (
                  <a
                    href={`https://maps.google.com/?q=${encodeURIComponent(mapsQuery)}`}
                    target="_blank" rel="noopener"
                    className="mt-3 flex items-center gap-1.5 text-xs font-semibold text-accent hover:underline"
                  >
                    <MapPin className="h-3.5 w-3.5" />
                    Ver no Google Maps
                  </a>
                )}
              </Section>
            )}

            {/* Dados eleitorais */}
            {hasElectoral && (
              <Section icon={Vote} title="Dados eleitorais">
                <div className="divide-y divide-hairline">
                  <DataRow label="Título de eleitor" value={contact.voter_registration} mono />
                  <DataRow label="Zona eleitoral" value={contact.electoral_zone} mono />
                  <DataRow label="Seção" value={contact.electoral_section} mono />
                </div>
              </Section>
            )}

            {/* Notas */}
            <Section icon={FileText} title="Notas">
              {notesEdit ? (
                <div className="space-y-3">
                  <textarea
                    value={notesDraft}
                    onChange={(e) => setNotesDraft(e.target.value)}
                    rows={5}
                    autoFocus
                    placeholder="Adicione notas sobre este contato…"
                    className="w-full rounded-xl bg-midnight-2 hairline px-4 py-3 text-sm text-ink placeholder-ink-faint outline-none focus:border-accent/40 resize-none transition-all"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={handleSaveNotes}
                      disabled={update.isPending}
                      className="flex items-center gap-1.5 rounded-xl btn-accent px-4 py-2 text-xs font-semibold"
                    >
                      {update.isPending
                        ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        : <Check className="h-3.5 w-3.5" />}
                      Salvar
                    </button>
                    <button
                      onClick={() => setNotesEdit(false)}
                      className="rounded-xl btn-ghost px-4 py-2 text-xs font-semibold"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  onClick={() => { setNotesDraft(contact.notes ?? ''); setNotesEdit(true); }}
                  className="group relative min-h-[80px] cursor-text rounded-xl p-3 -m-1 hover:bg-white/[0.03] transition-colors"
                >
                  {contact.notes ? (
                    <p className="text-sm text-ink leading-relaxed whitespace-pre-wrap">{contact.notes}</p>
                  ) : (
                    <p className="text-sm text-ink-faint italic">Clique para adicionar notas…</p>
                  )}
                  <Pencil className="absolute top-3 right-3 h-3.5 w-3.5 text-ink-subtle opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              )}
            </Section>

            {/* Notas de voz (gravação + transcrição Whisper) */}
            <Section icon={Mic} title="Notas de voz">
              <div className="space-y-4">
                <VoiceRecorder contactId={contact.id} />
                <VoiceNotesTimeline contactId={contact.id} />
              </div>
            </Section>
          </div>

          {/* Coluna lateral (1/3) */}
          <div className="space-y-5">

            {/* Tags */}
            <Section icon={Tag} title="Tags">
              {contact.tags && contact.tags.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {contact.tags.map((slug) => (
                    <TagBadge key={slug} slug={slug} tags={allTags} />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-ink-faint">Nenhuma tag</p>
              )}
              <button
                onClick={() => setEditOpen(true)}
                className="mt-1 text-xs font-semibold text-accent hover:underline"
              >
                + Gerenciar tags
              </button>
            </Section>

            {/* Liderança */}
            <Section icon={UserCheck} title="Liderança responsável">
              {contact.leader ? (
                <Link
                  to={`/app/contatos/${contact.leader.id}`}
                  className="flex items-center gap-3 rounded-xl hover:bg-white/[0.04] -mx-2 px-2 py-2 transition-colors group"
                >
                  <div className="h-9 w-9 shrink-0 rounded-xl bg-accent/10 hairline flex items-center justify-center font-bold text-sm text-accent">
                    {initials(contact.leader.full_name)}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-ink truncate">{contact.leader.full_name}</p>
                    <p className="text-[10px] text-ink-subtle">Ver perfil →</p>
                  </div>
                </Link>
              ) : (
                <div className="space-y-2">
                  <p className="text-sm text-ink-faint">Sem liderança atribuída</p>
                  <button
                    onClick={() => setEditOpen(true)}
                    className="text-xs font-semibold text-accent hover:underline"
                  >
                    + Atribuir liderança
                  </button>
                </div>
              )}
            </Section>

            {/* Metadados */}
            <Section icon={Clock} title="Metadados">
              <div className="space-y-3">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-ink-subtle mb-1">
                    ID do registro
                  </p>
                  <CopyableUUID value={contact.id} />
                </div>
                <div className="divide-y divide-hairline">
                  <DataRow label="Cadastrado em" value={fmtDateTime(contact.created_at)} />
                  <DataRow label="Atualizado em" value={fmtDateTime(contact.updated_at)} />
                </div>
                <div className="flex items-center gap-2 pt-1">
                  {contact.active
                    ? <><CheckCircle2 className="h-4 w-4 text-emerald-400" /><span className="text-xs text-emerald-400 font-semibold">Contato ativo</span></>
                    : <><XCircle className="h-4 w-4 text-danger" /><span className="text-xs text-danger font-semibold">Contato inativo</span></>}
                </div>
              </div>
            </Section>

          </div>
        </div>
      </div>

      {/* Modal de edição */}
      {editOpen && (
        <EditModal contactId={contact.id} onClose={() => setEditOpen(false)} />
      )}
    </>
  );
}
